import { getEntityReferenceTitle } from "@/utils/entity-reference.utils.ts";
import { CustomFieldEditProps } from "@/components/custom-field/edit/custom-field-edit.type.ts";
import { customFieldRenderers } from "@/components/custom-field/edit/custom-field-renderers.ts";
import { CustomFieldListItemCurrency } from "@/components/custom-field/list-item/CustomFieldListItemCurrency.tsx";
import { CustomFieldListItemDate } from "@/components/custom-field/list-item/CustomFieldListItemDate.tsx";
import { CustomFieldListItemDatetime } from "@/components/custom-field/list-item/CustomFieldListItemDatetime.tsx";
import { CustomFieldListItemEmployeeReference } from "@/components/custom-field/list-item/CustomFieldListItemEmployeeReference.tsx";
import { CustomFieldListItemEntityReference } from "@/components/custom-field/list-item/CustomFieldListItemEntityReference.tsx";
import { CustomFieldListItemFile } from "@/components/custom-field/list-item/CustomFieldListItemFile.tsx";
import { CustomFieldListItemNumber } from "@/components/custom-field/list-item/CustomFieldListItemNumber.tsx";
import { CustomFieldListItemPercentage } from "@/components/custom-field/list-item/CustomFieldListItemPercentage.tsx";
import { CustomFieldListItemSelect } from "@/components/custom-field/list-item/CustomFieldListItemSelect.tsx";
import { CustomFieldListItemSwitch } from "@/components/custom-field/list-item/CustomFieldListItemSwitch.tsx";
import { CustomFieldListItemText } from "@/components/custom-field/list-item/CustomFieldListItemText.tsx";
import { CustomFieldListItemTextarea } from "@/components/custom-field/list-item/CustomFieldListItemTextarea.tsx";
import {
  CustomFieldListItemUrl,
  CustomFieldUrlValue,
} from "@/components/custom-field/list-item/CustomFieldListItemUrl.tsx";
import { PendingSpinnerTooltip } from "@/components/pending-change/PendingSpinnerTooltip.tsx";
import { CompensationRollupValue } from "@/components/sensitive-value/CompensationRollupValue.tsx";
import { SalarySensitiveValue } from "@/components/sensitive-value/SalarySensitiveValue.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { CustomFieldFileItem } from "@/components/custom-field/CustomFieldFileItem.tsx";
import { ApiAppConfigUIEmployeeProfileSection } from "@/types/config.types.ts";
import {
  ApiCFTabType,
  ApiCustomField,
  ApiCustomFieldValue,
  ApiField,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiSection,
  ApiSectionTypes,
  ApiTable,
  ApiTableTypes,
  CustomFieldInformation,
} from "@/types/custom-field.types.ts";
import { getEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import {
  ApiEmployeeEditPayload,
  ApiEmployeeWriteToFieldChange,
  EmployeeGender,
} from "@/types/employees/employee.types.ts";
import { PermissionType } from "@/types/role.types.ts";
import {
  getCompensationRollupCellValue,
  isCompensationRollupField,
} from "@/utils/compensation-custom-field.utils.ts";
import { isValidDate } from "@fullcalendar/core/internal";
import {
  CustomFieldInput,
  getCustomFieldFormValues,
  getCustomFieldSchema,
  ListItem,
  type SelectItem,
  Stack,
  Text,
  toCustomFieldValue,
} from "@skailer-com/uikit";
import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export default function useCustomFieldsHook() {
  const { t } = useTranslation();
  const {
    renderDate,
    renderDateAndTime,
    renderCurrency,
    renderTimeAgo,
    renderEmployeeName,
    renderFileSize,
  } = useFormatter();

  const fieldTypeInfo: Record<string, CustomFieldInformation> = {
    [ApiFieldValueTypes.text]: {
      title: t("common:text"),
      description: t("custom_fields:text_description"),
      type: ApiFieldValueTypes.text,
    },
    [ApiFieldValueTypes.textarea]: {
      title: t("custom_fields:long_text_title"),
      description: t("custom_fields:long_text_description"),
      type: ApiFieldValueTypes.textarea,
    },
    [ApiFieldValueTypes.date]: {
      title: t("common:date"),
      description: t("custom_fields:date_description"),
      type: ApiFieldValueTypes.date,
    },
    [ApiFieldValueTypes.number]: {
      title: t("custom_fields:number_title"),
      description: t("custom_fields:number_description"),
      type: ApiFieldValueTypes.number,
    },
    [ApiFieldValueTypes.select]: {
      title: t("common:select"),
      description: t("custom_fields:single_select_description"),
      type: ApiFieldValueTypes.select,
    },
    [ApiFieldValueTypes.currency]: {
      title: t("common:currency"),
      description: t("custom_fields:currency_description"),
      type: ApiFieldValueTypes.currency,
    },
    [ApiFieldValueTypes.employee_reference]: {
      title: t("custom_fields:employee_reference_title"),
      description: t("custom_fields:employee_reference_description"),
      type: ApiFieldValueTypes.employee_reference,
    },
    [ApiFieldValueTypes.entity_reference]: {
      title: t("custom_fields:entity_reference_title"),
      description: t("custom_fields:entity_reference_description"),
      type: ApiFieldValueTypes.entity_reference,
    },
    [ApiFieldValueTypes.email]: {
      title: t("core:email"),
      description: t("custom_fields:email_description"),
      type: ApiFieldValueTypes.email,
    },
    [ApiFieldValueTypes.phone]: {
      title: t("core:phone"),
      description: t("custom_fields:phone_description"),
      type: ApiFieldValueTypes.phone,
    },
    [ApiFieldValueTypes.url]: {
      title: t("common:url"),
      description: t("custom_fields:url_description"),
      type: ApiFieldValueTypes.url,
    },
    [ApiFieldValueTypes.percentage]: {
      title: t("custom_fields:percentage_title"),
      description: t("custom_fields:percentage_description"),
      type: ApiFieldValueTypes.percentage,
    },
    [ApiFieldValueTypes.switch]: {
      title: t("custom_fields:switch_title"),
      description: t("custom_fields:switch_description"),
      type: ApiFieldValueTypes.switch,
    },
    [ApiFieldValueTypes.file]: {
      title: t("common:file"),
      description: t("custom_fields:file_description"),
      type: ApiFieldValueTypes.file,
    },
    [ApiFieldValueTypes.image]: {
      title: t("common:image"),
      description: t("custom_fields:image_description"),
      type: ApiFieldValueTypes.image,
    },
    // [ApiFieldValueTypes.datetime]: {
    //   title: t("common:date_and_time"),
    //   description: t("custom_fields:datetime_description"),
    //   type: ApiFieldValueTypes.datetime,
    // },
  };

  const getSchemaForField = (field: ApiField) => {
    const schemaFields: Record<string, yup.AnySchema> = {};
    let fieldSchema;

    switch (field.field_type) {
      case ApiFieldValueTypes.text:
      case ApiFieldValueTypes.textarea:
      case ApiFieldValueTypes.phone:
      case ApiFieldValueTypes.file:
      case ApiFieldValueTypes.image:
        fieldSchema = field.is_mandatory
          ? yup.string().required(t("errors:field_required"))
          : yup.string().nullable();
        break;
      case ApiFieldValueTypes.email: {
        const strEmail = yup
          .string()
          .transform((val, orig) => (orig === "" ? null : val))
          .email(t("errors:field_email_required"));

        fieldSchema = field.is_mandatory
          ? strEmail.nullable().required(t("errors:field_required"))
          : strEmail.nullable();
        break;
      }
      case ApiFieldValueTypes.url: {
        schemaFields[`${field.slug}_label`] = yup
          .string()
          .nullable()
          .max(
            255,
            t("errors:field_max_length", { characters: t("plurals:characters", { count: 255 }) })
          );
        const strUrl = yup
          .string()
          .transform((val, orig) => (orig === "" ? null : val))
          .url(t("errors:field_url_invalid"));

        fieldSchema = field.is_mandatory
          ? strUrl.nullable().required(t("errors:field_required"))
          : strUrl.nullable();
        break;
      }
      case ApiFieldValueTypes.number:
      case ApiFieldValueTypes.percentage:
        fieldSchema = field.is_mandatory
          ? yup
              .number()
              .required(t("errors:field_required"))
              .typeError(t("errors:field_number_required"))
          : yup
              .number()
              .typeError(t("errors:field_number_required"))
              .nullable()
              .transform((value, originalValue) => {
                return originalValue === "" ? null : value;
              });
        break;
      case ApiFieldValueTypes.date:
      case ApiFieldValueTypes.datetime:
        fieldSchema = field.is_mandatory
          ? yup.date().required(t("errors:field_required"))
          : yup.date().nullable();
        break;
      case ApiFieldValueTypes.switch:
        fieldSchema = yup.boolean().nullable();
        break;
      case ApiFieldValueTypes.select:
        fieldSchema = field.is_mandatory
          ? yup
              .array()
              .of(yup.mixed<number | string>())
              .required(t("errors:field_required"))
              .min(1, t("errors:field_required"))
          : yup.array().of(yup.mixed<number | string>()).nullable();
        break;
      case ApiFieldValueTypes.text_array:
        fieldSchema = field.is_mandatory
          ? yup
              .array()
              .of(yup.string().required())
              .required(t("errors:field_required"))
              .min(1, t("errors:field_required"))
          : yup.array().of(yup.string().required()).nullable();
        break;
      case ApiFieldValueTypes.currency:
        if (field.is_mandatory) {
          fieldSchema = yup
            .number()
            .required(t("errors:field_required"))
            .typeError(t("errors:field_number_required"));
          schemaFields[`${field.slug}_currency`] = yup
            .string()
            .nullable()
            .required(t("errors:field_required"));
        } else {
          fieldSchema = yup
            .number()
            .typeError(t("errors:field_number_required"))
            .nullable()
            .transform((value, originalValue) => {
              return originalValue === "" ? null : value;
            });
          schemaFields[`${field.slug}_currency`] = yup.string().nullable();
        }

        break;
      case ApiFieldValueTypes.employee_reference:
      case ApiFieldValueTypes.entity_reference:
        fieldSchema = field.is_mandatory
          ? yup
              .array()
              .of(yup.mixed<number>())
              .required(t("errors:field_required"))
              .min(1, t("errors:field_required"))
          : yup.array().of(yup.mixed<number>()).nullable();
        break;
      default:
        fieldSchema = yup.string().nullable();
    }

    schemaFields[field.slug] = fieldSchema;

    return schemaFields;
  };

  const getZodSchemaForField = (field: ApiField) => getCustomFieldSchema(field, t);

  const getValueForField = (
    field: ApiField,
    customFields?: ApiCustomField[],
    withDefaultValue = false
  ): Record<string, unknown> => {
    const customField = customFields?.find((f) => f.field.id === field.id);
    return getValueForFieldFromValueObject(field, customField?.value, withDefaultValue);
  };

  const getValueForFieldFromValueObject = useCallback(
    (field: ApiField, value: ApiCustomFieldValue | null | undefined, withDefaultValue = false) =>
      getCustomFieldFormValues(field, value, withDefaultValue),
    []
  );

  const getValuesForForm = (
    sections: ApiAppConfigUIEmployeeProfileSection[],
    customFields?: ApiCustomField[]
  ) => {
    let values: Record<string, unknown> = {};

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type == ApiFieldTypes.custom) {
          values = { ...values, ...getValueForField(field, customFields) };
        }
      });
    });

    return values;
  };

  const transformFormDataToPayload = (
    sections: ApiAppConfigUIEmployeeProfileSection[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form values keyed by runtime custom field slugs
    data: any
  ): ApiEmployeeEditPayload => {
    const fields = sections.reduce((acc, section) => acc.concat(section.fields), [] as ApiField[]);

    const payload: ApiEmployeeEditPayload = {};
    const custom_fields_values: Record<string, ApiCustomFieldValue> = {};

    Object.keys(data).forEach((key) => {
      const aimField = fields.find((item) => item.slug === key);
      const fieldData = data[key];

      if (aimField?.type != ApiFieldTypes.custom) {
        switch (aimField?.type) {
          case ApiFieldTypes.employee_first_name:
            payload.first_name = fieldData ?? "";
            break;
          case ApiFieldTypes.employee_name_pronunciation:
            payload.name_pronunciation = fieldData;
            break;
          case ApiFieldTypes.employee_last_name:
            payload.last_name = fieldData ?? "";
            break;
          case ApiFieldTypes.employee_middle_name:
            payload.middle_name = fieldData;
            break;
          case ApiFieldTypes.employee_phone:
            payload.phone = fieldData;
            break;
          case ApiFieldTypes.employee_phone_personal:
            payload.phone_personal = fieldData;
            break;
          case ApiFieldTypes.employee_email:
            payload.email = fieldData;
            break;
          case ApiFieldTypes.employee_email_personal:
            payload.email_personal = fieldData;
            break;
          case ApiFieldTypes.employee_number:
            payload.personnel_number = fieldData;
            break;
          case ApiFieldTypes.employee_address_address_line_1:
            payload.address_1 = fieldData;
            break;
          case ApiFieldTypes.employee_address_address_line_2:
            payload.address_2 = fieldData;
            break;
          case ApiFieldTypes.employee_address_city:
            payload.city = fieldData;
            break;
          case ApiFieldTypes.employee_address_zip_code:
            payload.zip = fieldData;
            break;
          case ApiFieldTypes.employee_address_state:
            payload.state = fieldData;
            break;
          case ApiFieldTypes.employee_address_country:
            payload.country_id = (fieldData as number[] | null)?.[0] ?? null;
            break;
          case ApiFieldTypes.employee_date_birth:
            payload.date_birth = fieldData;
            break;
          case ApiFieldTypes.employee_gender: {
            const genderString = (fieldData as string[]) ? fieldData[0] : null;
            payload.gender = genderString ? (genderString as EmployeeGender) : null;
            break;
          }
        }
      } else {
        custom_fields_values[aimField.slug] = transformFormDataToValue(aimField, data);
      }
    });

    payload.custom_fields = custom_fields_values;

    return payload;
  };

  const transformFormDataToValue = (field: ApiField, data: Record<string, unknown>) =>
    toCustomFieldValue(field, data) as ApiCustomFieldValue;

  const getCustomFieldPreviewValue = (
    field: ApiField,
    data: Record<string, unknown>,
    referenceOptions: Pick<SelectItem, "id" | "title">[] = []
  ): string => {
    const fieldData = data[field.slug];
    const isEmpty =
      fieldData === null ||
      fieldData === undefined ||
      fieldData === "" ||
      (Array.isArray(fieldData) && fieldData.length === 0);

    switch (field.field_type) {
      case ApiFieldValueTypes.text:
      case ApiFieldValueTypes.textarea:
      case ApiFieldValueTypes.phone:
      case ApiFieldValueTypes.email:
        return isEmpty ? "-" : String(fieldData);
      case ApiFieldValueTypes.url:
        return isEmpty
          ? "-"
          : data[`${field.slug}_label`]
            ? `${data[`${field.slug}_label`]} (${fieldData})`
            : String(fieldData);
      case ApiFieldValueTypes.number:
      case ApiFieldValueTypes.percentage:
        return isEmpty || Number.isNaN(Number(fieldData)) ? "-" : String(fieldData);
      case ApiFieldValueTypes.switch:
        return fieldData ? t("common:yes") : t("common:no");
      case ApiFieldValueTypes.select:
      case ApiFieldValueTypes.text_array: {
        if (isEmpty) return "-";
        const values = Array.isArray(fieldData) ? fieldData : [fieldData];
        return values
          .map((value) => {
            const option = field.options.find((item) => String(item.id) === String(value));
            return option?.title ?? String(value);
          })
          .join(", ");
      }
      case ApiFieldValueTypes.employee_reference: {
        const values = Array.isArray(fieldData) ? fieldData : [];
        return values.length > 0 ? String(values.length) : "-";
      }
      case ApiFieldValueTypes.entity_reference: {
        if (isEmpty) return "-";
        const values = Array.isArray(fieldData) ? fieldData : [fieldData];
        return values
          .map((value) => {
            const option = referenceOptions.find((item) => String(item.id) === String(value));
            return option?.title ?? String(value);
          })
          .join(", ");
      }
      case ApiFieldValueTypes.currency: {
        if (isEmpty || Number.isNaN(Number(fieldData))) return "-";
        const currencyCode = data[`${field.slug}_currency`];
        return currencyCode
          ? renderCurrency(String(fieldData), String(currencyCode))
          : String(fieldData);
      }
      case ApiFieldValueTypes.date: {
        if (isEmpty) return "-";
        const date = new Date(fieldData as string | number | Date);
        return isValidDate(date) ? renderDate(date) : "-";
      }
      case ApiFieldValueTypes.datetime: {
        if (isEmpty) return "-";
        const datetime = new Date(fieldData as string | number | Date);
        return isValidDate(datetime) ? renderDateAndTime(datetime) : "-";
      }
      case ApiFieldValueTypes.file:
      case ApiFieldValueTypes.image:
        return isEmpty ? "-" : t("common:changed");
      default:
        return isEmpty ? "-" : String(fieldData);
    }
  };

  const getPermissionForField = (
    field: ApiField,
    customFields?: ApiCustomField[]
  ): PermissionType => {
    return (
      customFields?.find((fieldWrapper) => fieldWrapper.field.id === field.id)?.permission_value ??
      PermissionType.none
    );
  };

  const getFieldTypeInformation = (
    type: ApiFieldValueTypes,
    fieldType?: ApiFieldTypes
  ): CustomFieldInformation => {
    if (fieldType === ApiFieldTypes.applicant_additional_contacts) {
      return { title: t("core:contacts"), description: "", type };
    }
    return fieldTypeInfo[type] ?? { title: "", description: "" };
  };

  const getFieldByType = (props: CustomFieldEditProps) => (
    <CustomFieldInput<ApiField, ApiCustomFieldValue>
      field={props.field}
      form={props.formHook}
      readOnly={!props.canEdit}
      label={props.label ?? getFieldTitle(props.field)}
      fieldValue={props.fieldValue}
      preFieldSlot={props.preFieldSlot}
      options={props.options}
      recentOptions={props.recentOptions}
      searchOptions={props.searchOptions}
      onSearchOptions={props.onSearchOptions}
      isLoading={props.isLoading}
      loadOptionsOnOpen={props.loadOptionsOnOpen}
      formatFileSize={renderFileSize}
      renderers={customFieldRenderers}
    />
  );

  const renderFieldDataInCell = (
    field: ApiField,
    customFields: ApiCustomField[],
    renderOptions?: {
      currencyTextClassName?: string;
      appendEllipsis?: boolean;
    }
  ) => {
    const customField = customFields.find((employeeField) => employeeField.field.id === field.id);
    const fieldValue = customField?.value ?? undefined;

    if (isCompensationRollupField(field.type)) {
      const { amount, currency } = getCompensationRollupCellValue(field.id, customFields);

      return (
        <CompensationRollupValue
          value={amount == null ? null : renderCurrency(String(amount), currency)}
          textClassName={renderOptions?.currencyTextClassName}
        />
      );
    }

    switch (field.field_type) {
      case ApiFieldValueTypes.text:
      case ApiFieldValueTypes.phone:
      case ApiFieldValueTypes.email:
      case ApiFieldValueTypes.textarea:
        return fieldValue?.value_text || "-";
      case ApiFieldValueTypes.url:
        return fieldValue?.value_text ? (
          <CustomFieldUrlValue url={fieldValue.value_text} label={fieldValue.value_label} />
        ) : (
          "-"
        );
      case ApiFieldValueTypes.number:
        return typeof fieldValue?.value_int == "undefined" ? "-" : (fieldValue?.value_int ?? "-");
      case ApiFieldValueTypes.percentage:
        return !fieldValue ||
          typeof fieldValue?.value_float == "undefined" ||
          fieldValue?.value_float == null
          ? "-"
          : `${fieldValue?.value_float}%`;
      case ApiFieldValueTypes.date:
        return fieldValue?.date_value ? renderDate(fieldValue?.date_value) : "-";
      case ApiFieldValueTypes.employee_reference:
        return (
          <Stack>
            {fieldValue?.value_employees ? (
              fieldValue?.value_employees?.map((employee) => (
                <EmployeeListItem
                  employee={employee}
                  behavior={"card"}
                  emphasis={renderOptions?.appendEllipsis ? "top" : undefined}
                  title={
                    renderOptions?.appendEllipsis
                      ? `${renderEmployeeName(employee)}...`
                      : renderEmployeeName(employee)
                  }
                  key={`employee_ref_${employee.id}`}
                />
              ))
            ) : (
              <ListItem title={"-"} forceShowAvatar={true} behavior={"card"} />
            )}
          </Stack>
        );
      case ApiFieldValueTypes.entity_reference:
        return fieldValue?.value_references?.length
          ? fieldValue.value_references
              .map((reference) =>
                getEntityReferenceTitle(reference, t("custom_fields:reference_archived"))
              )
              .join(", ")
          : "-";
      case ApiFieldValueTypes.file:
      case ApiFieldValueTypes.image:
        return fieldValue?.value_file ? <CustomFieldFileItem file={fieldValue?.value_file} /> : "-";
      case ApiFieldValueTypes.currency:
        return !fieldValue ||
          typeof fieldValue?.value_float == "undefined" ||
          fieldValue?.value_float == null ? (
          "-"
        ) : (
          <SalarySensitiveValue
            value={renderCurrency(`${fieldValue?.value_float}`, fieldValue?.value_text ?? null)}
            textClassName={renderOptions?.currencyTextClassName}
          />
        );
      case ApiFieldValueTypes.switch:
        return !fieldValue || typeof fieldValue?.value_bool == "undefined"
          ? "-"
          : fieldValue?.value_bool
            ? t("common:yes")
            : t("common:no");
      case ApiFieldValueTypes.select: {
        const options = fieldValue?.value_int_array?.length
          ? fieldValue.value_int_array
          : fieldValue?.value_int != null
            ? [fieldValue.value_int]
            : [];
        const selected = customField?.field.options
          ? customField?.field.options?.filter((option) => option.id && options.includes(option.id))
          : [];

        return selected.length ? selected.map((option) => option.title).join(", ") : "-";
      }
      case ApiFieldValueTypes.text_array: {
        let fieldOptions: SelectItem[] | undefined;
        switch (field.type) {
          case ApiFieldTypes.employee_compensation_per_type:
            fieldOptions = getEmployeeCompensationPerTypes();
            break;
        }

        if (!fieldValue?.value_text_array?.length) return "-";
        if (!fieldOptions) return fieldValue.value_text_array.join(", ");

        return fieldValue.value_text_array
          .map((value) => fieldOptions.find((option) => option.id === value)?.title ?? value)
          .join(", ");
      }
    }
  };

  const renderPendingValueText = (field: ApiField, value: ApiCustomFieldValue | null): string => {
    if (!value) return "-";
    switch (field.field_type) {
      case ApiFieldValueTypes.text:
      case ApiFieldValueTypes.textarea:
      case ApiFieldValueTypes.phone:
      case ApiFieldValueTypes.email:
        return value.value_text || "-";
      case ApiFieldValueTypes.url:
        return value.value_text
          ? value.value_label
            ? `${value.value_label} (${value.value_text})`
            : value.value_text
          : "-";
      case ApiFieldValueTypes.number:
        return value.value_int != null ? String(value.value_int) : "-";
      case ApiFieldValueTypes.percentage:
        return value.value_float != null ? `${value.value_float}%` : "-";
      case ApiFieldValueTypes.currency:
        return value.value_float != null
          ? renderCurrency(String(value.value_float), value.value_text ?? null)
          : "-";
      case ApiFieldValueTypes.date:
        return value.date_value ? renderDate(value.date_value) : "-";
      case ApiFieldValueTypes.datetime:
        return value.datetime_value ? renderDateAndTime(value.datetime_value) : "-";
      case ApiFieldValueTypes.switch:
        return value.value_bool ? t("common:yes") : t("common:no");
      case ApiFieldValueTypes.select: {
        const ids = value.value_int_array ?? [];
        const titles = (field.options ?? [])
          .filter((option) => option.id && ids.includes(option.id))
          .map((option) => option.title);
        return titles.length ? titles.join(", ") : "-";
      }
      case ApiFieldValueTypes.text_array:
        return value.value_text_array?.length ? value.value_text_array.join(", ") : "-";
      case ApiFieldValueTypes.employee_reference:
        return value.value_employees?.length
          ? value.value_employees.map((employee) => renderEmployeeName(employee)).join(", ")
          : "-";
      case ApiFieldValueTypes.entity_reference:
        return value.value_references?.length
          ? value.value_references.map((reference) => reference.title).join(", ")
          : "-";
      case ApiFieldValueTypes.file:
      case ApiFieldValueTypes.image:
        return value.value_file?.name ?? "-";
      default:
        return "-";
    }
  };

  const renderPendingTooltipContent = (
    createdAt: Date,
    diff?: { currentValue: ReactNode; newValue: ReactNode }
  ): ReactNode => {
    return (
      <Stack gap="xs">
        <Text className="text-light">{t("common:pending_change_waiting_approval")}</Text>
        {diff && (
          <Stack direction="horizontal" gap="xs" items="center">
            <Text className="text-light/80 font-bold">{diff.currentValue}</Text>
            <span className="text-light/80 font-bold">→</span>
            <Text className="text-light font-bold">{diff.newValue}</Text>
          </Stack>
        )}
        <Text className="text-light/80">
          {t("workflow:created_ago", { time: renderTimeAgo(createdAt) })}
        </Text>
      </Stack>
    );
  };

  const renderPendingListItem = (
    label: string,
    rowValue: ReactNode,
    currentValue: ReactNode,
    newValue: ReactNode,
    createdAt: Date,
    skipEllipsis = false
  ): ReactNode => {
    const displayValue =
      skipEllipsis || rowValue === "-" ? (
        rowValue
      ) : (
        <>
          {rowValue}
          {"..."}
        </>
      );
    return (
      <ListItem
        preTitle={label}
        titleSlot={<div className="w-fit text-secondary">{displayValue}</div>}
        valueSlot={
          <PendingSpinnerTooltip
            tooltip={renderPendingTooltipContent(createdAt, { currentValue, newValue })}
            className="w-fit"
          />
        }
      />
    );
  };

  const renderCustomFieldListItem = (
    field: ApiField,
    customFields: ApiCustomField[],
    pendingChange?: ApiEmployeeWriteToFieldChange
  ): ReactNode | undefined => {
    const customField = customFields.find((fieldWrapper) => fieldWrapper.field.id == field.id);
    let item: ReactNode | undefined;
    const label = getFieldTitle(field);
    const fieldValue = customField?.value ?? undefined;
    const isAlwaysVisible = field.is_always_visible;

    if (pendingChange) {
      const isEmployeeReference = field.field_type === ApiFieldValueTypes.employee_reference;
      const isCurrency = field.field_type === ApiFieldValueTypes.currency;
      const pendingCustomFields: ApiCustomField[] = [
        { field, value: pendingChange.value_after, permission_value: PermissionType.edit },
      ];
      let rowValue = renderFieldDataInCell(field, pendingCustomFields, {
        currencyTextClassName: "text-secondary",
        appendEllipsis: isEmployeeReference,
      });
      if (isCurrency && rowValue !== "-") {
        rowValue = (
          <Stack direction="horizontal" items="center" className="w-fit">
            {rowValue}
            {"..."}
          </Stack>
        );
      }
      return renderPendingListItem(
        label,
        rowValue,
        renderPendingValueText(field, customField?.value ?? null),
        renderPendingValueText(field, pendingChange.value_after),
        pendingChange.datetime_created_at,
        isEmployeeReference || isCurrency
      );
    }

    switch (field.field_type) {
      case ApiFieldValueTypes.textarea:
        if (isAlwaysVisible || (fieldValue?.value_text && fieldValue.value_text.length > 0)) {
          item = (
            <CustomFieldListItemTextarea field={field} fieldValue={fieldValue} label={label} />
          );
        }
        break;
      case ApiFieldValueTypes.text:
      case ApiFieldValueTypes.email:
      case ApiFieldValueTypes.phone:
        if (isAlwaysVisible || (fieldValue?.value_text && fieldValue.value_text.length > 0)) {
          item = <CustomFieldListItemText field={field} fieldValue={fieldValue} label={label} />;
        }
        break;
      case ApiFieldValueTypes.url:
        if (isAlwaysVisible || (fieldValue?.value_text && fieldValue.value_text.length > 0)) {
          item = <CustomFieldListItemUrl field={field} fieldValue={fieldValue} label={label} />;
        }
        break;
      case ApiFieldValueTypes.number:
        if (isAlwaysVisible || fieldValue?.value_int != null) {
          item = <CustomFieldListItemNumber field={field} fieldValue={fieldValue} label={label} />;
        }
        break;
      case ApiFieldValueTypes.percentage:
        if (isAlwaysVisible || fieldValue?.value_float != null) {
          item = (
            <CustomFieldListItemPercentage field={field} fieldValue={fieldValue} label={label} />
          );
        }
        break;
      case ApiFieldValueTypes.select:
        if (isAlwaysVisible || fieldValue?.value_int_array) {
          item = (
            <CustomFieldListItemSelect
              field={field}
              fieldValue={fieldValue}
              options={(customField?.field ?? field).options.map((option) => ({
                id: Number(option.id),
                title: option.title,
              }))}
              label={label}
            />
          );
        }
        break;
      case ApiFieldValueTypes.text_array:
        if (isAlwaysVisible || fieldValue?.value_text_array?.length) {
          item = (
            <ListItem preTitle={label} title={fieldValue?.value_text_array?.join(", ") || "-"} />
          );
        }
        break;
      case ApiFieldValueTypes.switch:
        if (isAlwaysVisible || fieldValue) {
          item = <CustomFieldListItemSwitch field={field} fieldValue={fieldValue} label={label} />;
        }
        break;
      case ApiFieldValueTypes.currency:
        if (isAlwaysVisible || fieldValue?.value_float != null) {
          item = (
            <CustomFieldListItemCurrency field={field} fieldValue={fieldValue} label={label} />
          );
        }

        break;
      case ApiFieldValueTypes.date:
        if (isAlwaysVisible || fieldValue?.date_value) {
          item = <CustomFieldListItemDate field={field} fieldValue={fieldValue} label={label} />;
        }

        break;
      case ApiFieldValueTypes.datetime:
        if (isAlwaysVisible || fieldValue?.datetime_value) {
          item = (
            <CustomFieldListItemDatetime field={field} fieldValue={fieldValue} label={label} />
          );
        }

        break;
      case ApiFieldValueTypes.employee_reference:
        if (isAlwaysVisible || fieldValue?.value_employees?.length) {
          item = (
            <CustomFieldListItemEmployeeReference
              field={field}
              fieldValue={fieldValue}
              label={label}
            />
          );
        }

        break;
      case ApiFieldValueTypes.entity_reference:
        if (isAlwaysVisible || fieldValue?.value_references?.length) {
          item = (
            <CustomFieldListItemEntityReference
              field={field}
              fieldValue={fieldValue}
              label={label}
            />
          );
        }
        break;
      case ApiFieldValueTypes.image:
      case ApiFieldValueTypes.file:
        if (isAlwaysVisible || fieldValue?.value_file) {
          item = <CustomFieldListItemFile field={field} fieldValue={fieldValue} label={label} />;
        }

        break;
    }

    return item;
  };

  const getFieldTitle = (field: Pick<ApiField, "type" | "title">) => {
    if (field.type !== ApiFieldTypes.custom && field.title?.trim()) {
      return field.title;
    }

    let title = "";

    switch (field.type) {
      case ApiFieldTypes.effective_from:
        title = t("common:date_effective_from");
        break;
      case ApiFieldTypes.effective_to:
        title = t("common:date_effective_to");
        break;
      case ApiFieldTypes.changed_by:
        title = t("core:changed_by");
        break;
      case ApiFieldTypes.employee_position_job_title:
        title = t("ats:job_title");
        break;
      case ApiFieldTypes.employee_position_job_profile:
        title = t("core:job_profile");
        break;
      case ApiFieldTypes.employee_position_reporting_to:
        title = t("core:manager");
        break;
      case ApiFieldTypes.employee_employment_legal_entity:
        title = t("core:legal_entity");
        break;
      case ApiFieldTypes.employee_employment_type:
        title = t("core:employment_type");
        break;
      case ApiFieldTypes.employee_employment_contract:
        title = t("core:employment_contract");
        break;
      case ApiFieldTypes.employee_employment_work_pattern:
        title = t("core:work_pattern");
        break;
      case ApiFieldTypes.employee_employment_comment:
        title = t("common:comment");
        break;
      case ApiFieldTypes.employee_compensation_base_salary_gross:
        title = t("core:base_salary_gross");
        break;
      case ApiFieldTypes.employee_compensation_base_salary_net:
        title = t("core:base_salary_net");
        break;
      case ApiFieldTypes.employee_compensation_gross_total:
        title = t("core:compensation_gross_rollup");
        break;
      case ApiFieldTypes.employee_compensation_net_total:
        title = t("core:compensation_net_official_rollup");
        break;
      case ApiFieldTypes.employee_compensation_total_cost:
        title = t("core:compensation_total_cost_rollup");
        break;
      case ApiFieldTypes.employee_compensation_lines:
        title = t("core:compensation");
        break;
      case ApiFieldTypes.employee_compensation_currency:
        title = t("common:currency");
        break;
      case ApiFieldTypes.employee_compensation_per_type:
        title = t("common:period");
        break;
      case ApiFieldTypes.employee_compensation_change_reason:
        title = t("core:compensation_change_reason");
        break;
      case ApiFieldTypes.employee_compensation_comment:
        title = t("common:comment");
        break;
      case ApiFieldTypes.employee_additional_compensation_compensation_type:
        title = t("core:compensation_type");
        break;
      case ApiFieldTypes.employee_additional_compensation_amount_gross:
        title = t("core:amount_money_gross");
        break;
      case ApiFieldTypes.employee_additional_compensation_amount_net:
        title = t("core:amount_money_net");
        break;
      case ApiFieldTypes.employee_additional_compensation_currency:
        title = t("common:currency");
        break;
      case ApiFieldTypes.employee_additional_compensation_description:
        title = t("common:description");
        break;
      case ApiFieldTypes.employee_additional_compensation_comment:
        title = t("common:comment");
        break;
      case ApiFieldTypes.employee_position_division:
        title = t("core:division");
        break;
      case ApiFieldTypes.employee_position_location:
        title = t("common:location");
        break;
      case ApiFieldTypes.employee_position_department:
        title = t("core:department");
        break;
      case ApiFieldTypes.employee_position_change_reason:
        title = t("core:employee_position_change_reason");
        break;
      case ApiFieldTypes.employee_position_comment:
        title = t("common:comment");
        break;
      case ApiFieldTypes.employee_number:
        title = t("core:employee_number");
        break;
      case ApiFieldTypes.employee_hired_on:
        title = t("core:hired_on");
        break;
      case ApiFieldTypes.employee_employment_probation_date_end:
        title = t("core:probation_till");
        break;
      case ApiFieldTypes.employee_lifecycle_status:
        title = t("core:lifecycle_status");
        break;
      case ApiFieldTypes.employee_last_activity_date:
        title = t("core:last_activity_date");
        break;
      case ApiFieldTypes.employee_timezone:
        title = t("common:timezone");
        break;
      case ApiFieldTypes.employee_first_name:
        title = t("core:first_name");
        break;
      case ApiFieldTypes.employee_last_name:
        title = t("core:last_name");
        break;
      case ApiFieldTypes.employee_middle_name:
        title = t("core:middle_name");
        break;
      case ApiFieldTypes.employee_name_pronunciation:
        title = t("common:name_pronunciation");
        break;
      case ApiFieldTypes.employee_gender:
        title = t("core:gender");
        break;
      case ApiFieldTypes.employee_date_birth:
        title = t("core:birth_date");
        break;
      case ApiFieldTypes.employee_email:
        title = t("core:work_email");
        break;
      case ApiFieldTypes.employee_email_personal:
        title = t("core:email_personal");
        break;
      case ApiFieldTypes.employee_phone:
        title = t("core:phone");
        break;
      case ApiFieldTypes.employee_phone_personal:
        title = t("core:phone_personal");
        break;
      case ApiFieldTypes.employee_slack:
        title = t("integrations:slack_title");
        break;
      case ApiFieldTypes.employee_address_address_line_1:
        title = t("core:address_line_1");
        break;
      case ApiFieldTypes.employee_address_address_line_2:
        title = t("core:address_line_2");
        break;
      case ApiFieldTypes.employee_address_country:
        title = t("core:country");
        break;
      case ApiFieldTypes.employee_address_state:
        title = t("core:state");
        break;
      case ApiFieldTypes.employee_address_city:
        title = t("core:city");
        break;
      case ApiFieldTypes.employee_address_zip_code:
        title = t("core:zip");
        break;
      case ApiFieldTypes.applicant_cv:
        title = t("ats:resume");
        break;
      case ApiFieldTypes.applicant_first_name:
        title = t("core:first_name");
        break;
      case ApiFieldTypes.applicant_position:
        title = t("core:position");
        break;
      case ApiFieldTypes.applicant_company:
        title = t("common:company");
        break;
      case ApiFieldTypes.applicant_last_name:
        title = t("core:last_name");
        break;
      case ApiFieldTypes.applicant_photo:
        title = t("common:photo");
        break;
      case ApiFieldTypes.applicant_salary:
        title = t("core:salary");
        break;
      case ApiFieldTypes.applicant_middle_name:
        title = t("core:middle_name");
        break;
      case ApiFieldTypes.applicant_location:
        title = t("common:location");
        break;
      case ApiFieldTypes.applicant_email:
        title = t("core:email");
        break;
      case ApiFieldTypes.applicant_telegram:
        title = t("common:telegram");
        break;
      case ApiFieldTypes.applicant_phone:
        title = t("core:phone");
        break;
      case ApiFieldTypes.applicant_date_birth:
        title = t("core:birth_date");
        break;
      case ApiFieldTypes.applicant_additional_contacts:
        title = t("ats:additional_contacts");
        break;
      case ApiFieldTypes.job_title:
        title = t("common:title");
        break;
      case ApiFieldTypes.job_public_title:
        title = t("ats:public_title");
        break;
      case ApiFieldTypes.job_department:
        title = t("core:department");
        break;
      case ApiFieldTypes.job_division:
        title = t("core:division");
        break;
      case ApiFieldTypes.job_job_profile:
        title = t("core:job_profile");
        break;
      case ApiFieldTypes.job_location:
        title = t("common:location");
        break;
      case ApiFieldTypes.job_priority:
        title = t("ats:job_priority");
        break;
      case ApiFieldTypes.job_pipeline:
        title = t("ats:pipeline");
        break;
      case ApiFieldTypes.job_approval_policy:
        title = t("approval:approval_policy");
        break;
      case ApiFieldTypes.job_recruiters:
        title = t("ats:recruiters");
        break;
      case ApiFieldTypes.job_stakeholders:
        title = t("ats:stakeholders");
        break;
      case ApiFieldTypes.job_salary_min:
        title = t("ats:min_salary");
        break;
      case ApiFieldTypes.job_salary_max:
        title = t("ats:max_salary");
        break;
      case ApiFieldTypes.job_currency:
        title = t("common:currency");
        break;
      case ApiFieldTypes.job_description:
        title = t("common:description");
        break;
      case ApiFieldTypes.job_requirements:
        title = t("hiring_plan:requirements");
        break;
      case ApiFieldTypes.job_responsibilities:
        title = t("hiring_plan:responsibilities");
        break;
      case ApiFieldTypes.job_conditions:
        title = t("hiring_plan:conditions");
        break;
      case ApiFieldTypes.job_files:
        title = t("common:files");
        break;
      case ApiFieldTypes.offer_first_name:
        title = t("core:first_name");
        break;
      case ApiFieldTypes.offer_last_name:
        title = t("core:last_name");
        break;
      case ApiFieldTypes.offer_job_title:
        title = t("ats:job_title");
        break;
      case ApiFieldTypes.offer_date_start:
        title = t("ats:planned_start");
        break;
      case ApiFieldTypes.offer_legal_entity:
        title = t("core:legal_entity");
        break;
      case ApiFieldTypes.offer_location:
        title = t("common:location");
        break;
      case ApiFieldTypes.offer_manager:
        title = t("ats:managed_by");
        break;
      case ApiFieldTypes.offer_salary:
        title = t("core:salary");
        break;
      case ApiFieldTypes.offer_text:
        title = t("ats:offer_text");
        break;
      case ApiFieldTypes.employee_employment_termination_date:
        title = t("core:termination_date");
        break;
      case ApiFieldTypes.employee_termination_last_working_date:
        title = t("core:last_working_day");
        break;
      case ApiFieldTypes.employee_termination_leave_type:
        title = t("core:leave_type");
        break;
      case ApiFieldTypes.employee_termination_reason:
        title = t("core:termination_reason");
        break;
      case ApiFieldTypes.employee_termination_replacement_employee:
        title = t("core:termination_handover_to");
        break;
      case ApiFieldTypes.employee_termination_eligible_for_rehire:
        title = t("core:eligible_for_rehire");
        break;
      case ApiFieldTypes.employee_termination_comment:
        title = t("common:comments");
        break;
      case ApiFieldTypes.custom:
        title = field.title;
        break;
    }

    return title;
  };

  const getTabTitle = (tab: { type: ApiCFTabType }) => {
    let title = "";

    switch (tab.type) {
      case ApiCFTabType.sidebar:
        title = t("custom_fields:employee_tab_sidebar_title");
        break;
      case ApiCFTabType.overview:
        title = t("common:overview");
        break;
      case ApiCFTabType.work_info:
        title = t("core:work_info");
        break;
    }

    return title;
  };

  const getSectionTitle = (section: ApiSection) => {
    let title = "";

    switch (section.section_type) {
      case ApiSectionTypes.employee_basic_info_manager:
        title = t("core:manager");
        break;
      case ApiSectionTypes.employee_basic_info_contacts:
      case ApiSectionTypes.applicant_contacts:
        title = t("core:contacts");
        break;
      case ApiSectionTypes.applicant_personal_info:
      case ApiSectionTypes.employee_basic_info_personal:
        title = t("core:personal");
        break;
      case ApiSectionTypes.employee_basic_info_address:
        title = t("core:address");
        break;
      case ApiSectionTypes.employee_basic_info_hired_via_job:
        title = t("custom_fields:hired_via_job");
        break;
      case ApiSectionTypes.employee_overview_summary:
        title = t("common:summary");
        break;
      case ApiSectionTypes.employee_overview_direct_reports:
        title = t("core:direct_reports");
        break;
      case ApiSectionTypes.employee_overview_teams:
        title = t("core:teams");
        break;
      case ApiSectionTypes.employee_overview_department_employees:
        title = t("core:department");
        break;
      case ApiSectionTypes.employee_overview_recognitions:
      case ApiSectionTypes.employee_basic_info_recognitions:
        title = t("recognition:profile_title");
        break;
      case ApiSectionTypes.applicant_upload_files:
        title = t("ats:upload_files");
        break;
      case ApiSectionTypes.applicant_experience:
        title = t("ats:experience");
        break;
      case ApiSectionTypes.job_details:
        title = t("ats:job_summary");
        break;
      case ApiSectionTypes.job_hiring_process:
        title = t("custom_fields:job_hiring_process");
        break;
      case ApiSectionTypes.job_compensation:
        title = t("core:compensation");
        break;
      case ApiSectionTypes.job_description:
        title = t("common:description");
        break;
      case ApiSectionTypes.offer_candidate:
        title = t("ats:candidate");
        break;
      case ApiSectionTypes.offer_terms:
        title = t("ats:offer_details");
        break;
      case ApiSectionTypes.offer_text:
        title = t("ats:offer_text");
        break;
      case ApiSectionTypes.custom:
        title = section.title;
        break;
    }

    return title;
  };

  const getTableTitle = (table: Pick<ApiTable, "table_type" | "title">) => {
    let title = "";

    switch (table.table_type) {
      case ApiTableTypes.employee_positions:
        title = t("core:position");
        break;
      case ApiTableTypes.employee_employments:
        title = t("core:employment");
        break;
      case ApiTableTypes.employee_terminations:
        title = t("core:termination");
        break;
      case ApiTableTypes.employee_compensations:
        title = t("core:compensation");
        break;
      case ApiTableTypes.employee_additional_compensations:
        title = t("core:additional_compensation");
        break;
      case ApiTableTypes.custom:
        title = table.title;
        break;
    }

    return title;
  };

  return {
    getPermissionForField,
    getFieldTypeInformation,
    fieldTypeInfo,
    getSchemaForField,
    getZodSchemaForField,
    getValuesForForm,
    getValueForField,
    transformFormDataToPayload,
    transformFormDataToValue,
    getCustomFieldPreviewValue,
    getFieldByType,
    renderFieldDataInCell,
    renderCustomFieldListItem,
    renderPendingListItem,
    renderPendingTooltipContent,
    renderPendingValueText,
    getFieldTitle,
    getTabTitle,
    getSectionTitle,
    getTableTitle,
    getValueForFieldFromValueObject,
  };
}
